import { defineStore } from 'pinia';
import { HttpStatusCode } from 'axios';
import { http } from '@/http';
import { useNotification } from '@kyvg/vue3-notification';
import { useLoading } from 'vue-loading-overlay';
import { useAuthStore } from '@/stores';

const baseUrl = `/api/v1/users`;
const debug = false;
const loading = useLoading({
    canCancel: false,
    isFullPage: true,
    color: '#f1e7bb',
    loader: 'dots',
    width: 64,
    height: 64,
    backgroundColor: '#000000',
    opacity: 0.3,
    zIndex: 1050,
});

export const useUserStore = defineStore({
    id: 'users',
    state: () => ({
        // initialize state from local storage to enable user to stay logged in
        users: [],
        user:{},
        has_more:true,
        curPage:1
    }),
    actions: {
        async getSomeUsers(page=1, per=10, filter='') {
            try {
                const query = `page=${page}&per=${per}${filter ? `&filter=${encodeURIComponent(filter)}` : ''}`;
                let resp = await http.get(`${baseUrl}?${query}`);
                if (resp.status == HttpStatusCode.Ok) {
                    this.curPage = page;
                    this.has_more = resp.data.items.length == per;
                    this.users = resp.data.items;
                    return true;
                } else {
                    return false;
                }
            } catch (err) {
                const { notify } = useNotification()
                notify({
                    title: 'Список пользователей',
                    text: err,
                    type: 'error'
                });
                console.error(`useUserStore.getSomeUsers ${err}`);
            }
        },
        async me() {
            const loader = loading.show();
            try {
                let resp = await http.get(`${baseUrl}/me`);
                console.log(`${baseUrl}/me resp = ${resp.status}`);
                if (resp.status == HttpStatusCode.Ok && resp.data) {
                    this.storeUser(resp);
                    return true;
                } else {
                    return false;
                }    
            } catch (err) {
                this.clearUser();
                const { notify } = useNotification()
                notify({
                    title: 'Данные пользователя',
                    text: err,
                    type: 'error'    
                });
                console.error(`useUserStore.user ${err}`);
                if (!debug) {
                    return false;
                } else {    
                    this.user = 
                    {
                    };
                    return true;
                }
            } finally {
                loader.hide();
            }
        },
        async updateUser(id,userData) {
            const loader = loading.show();
            try {
                let savingMyself = (this.user.id === userData.id); 
                let resp = await http.patch(`${baseUrl}/${id}`,userData);
                console.log(`PATCH ${baseUrl}/users/id resp = ${resp.status}`);
                if (resp.status == HttpStatusCode.Ok && resp.data) {
                    // if (this.users.length) { // update already read users
                    //     this.users.find(o=>{
                    //         if (o.id === resp.data.id) {
                    //             Object.assign(o,resp.data);
                    //         }
                    //     })
                    // }
                    if (savingMyself){
                        this.storeUser(resp);
                    }
                    return true;
                } else {
                    return false;
                }    
            } catch (err) {
                const { notify } = useNotification()
                notify({
                    title: 'Данные пользователя',
                    text: err,
                    type: 'error'    
                });
                console.error(`userStore.updateUser ${err}`);
                return false;
            } finally {
                loader.hide();
            }
        },
        async addUser(userData) {
            const loader = loading.show();
            try {
                let resp = await http.post(`${baseUrl}`,userData);
                console.log(`POST ${baseUrl} resp = ${resp.status}`);
                if (resp.status == HttpStatusCode.Ok && resp.data) {
                    return resp.data;
                } else {
                    return null;
                }
            } catch (err) {
                const { notify } = useNotification()
                notify({
                    title: 'Данные пользователя',
                    text: err,
                    type: 'error'    
                });
                console.error(`userStore.updateUser ${err}`);
                return false;
            } finally {
                loader.hide();
            }
        },
        storeUser(resp){
            this.user = resp.data;
            const authStore = useAuthStore();
            authStore.user = this.user;
            console.log(`storing user ${this.user}`);
            localStorage.setItem('user',JSON.stringify(this.user));
        },
        clearUser(){
            localStorage.removeItem('user');
            const authStore = useAuthStore();
            console.log(`removing user ${authStore.user}`);
            authStore.user = null;
            this.user = null;
        },
        async deleteUser(id) {
            const loader = loading.show();
            try {
                let resp = await http.delete(`${baseUrl}/${id}`);
                console.log(`DELETE ${baseUrl}/${id} resp = ${resp.status}`);
                if (resp.status == HttpStatusCode.Ok || resp.status == HttpStatusCode.NoContent) {
                    const { notify } = useNotification();
                    notify({
                        title: 'Пользователь',
                        text: 'Пользователь успешно удалён',
                        type: 'success'
                    });
                    return true;
                } else {
                    return false;
                }
            } catch (err) {
                const { notify } = useNotification();
                notify({
                    title: 'Удаление пользователя',
                    text: err,
                    type: 'error'
                });
                console.error(`userStore.deleteUser ${err}`);
                return false;
            } finally {
                loader.hide();
            }
        },
        async setPermanentCode(id, code) {
            const loader = loading.show();
            try {
                let resp = await http.put(`${baseUrl}/${id}/permanent-code`, { code });
                console.log(`PUT ${baseUrl}/${id}/permanent-code resp = ${resp.status}`);
                if (resp.status == HttpStatusCode.Ok || resp.status == HttpStatusCode.Created || resp.status == HttpStatusCode.NoContent) {
                    const { notify } = useNotification();
                    notify({
                        title: 'Постоянный код',
                        text: 'Постоянный код сохранён',
                        type: 'success'
                    });
                    return true;
                } else {
                    return false;
                }
            } catch (err) {
                const { notify } = useNotification();
                notify({
                    title: 'Постоянный код',
                    text: err,
                    type: 'error'
                });
                console.error(`userStore.setPermanentCode ${err}`);
                return false;
            } finally {
                loader.hide();
            }
        },
        async deletePermanentCode(id) {
            const loader = loading.show();
            try {
                let resp = await http.delete(`${baseUrl}/${id}/permanent-code`);
                console.log(`DELETE ${baseUrl}/${id}/permanent-code resp = ${resp.status}`);
                if (resp.status == HttpStatusCode.Ok || resp.status == HttpStatusCode.NoContent) {
                    const { notify } = useNotification();
                    notify({
                        title: 'Постоянный код',
                        text: 'Постоянный код удалён',
                        type: 'success'
                    });
                    return true;
                } else {
                    return false;
                }
            } catch (err) {
                const { notify } = useNotification();
                notify({
                    title: 'Постоянный код',
                    text: err,
                    type: 'error'
                });
                console.error(`userStore.deletePermanentCode ${err}`);
                return false;
            } finally {
                loader.hide();
            }
        },
        findUser(id) {
            if (!this.users.length) return null;
            const u = this.users.find(obj=>obj.id===id);
            return u;
        },
    },
    getters:{
        hasMore(state) {
            return state
        }
    }
});    
